import { Suspense, lazy } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/query-client';
import { AuthProvider, ADMIN_ROLES } from '@/lib/auth';
import { ThemeProvider } from '@/lib/theme';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/toast';
import { Spinner } from '@/components/ui/misc';
import { AppShell } from '@/components/layout/app-shell';
import { RequireAuth, RequireRole } from '@/routes/guards';
import { RouteErrorBoundary } from '@/routes/error-boundary';

// Cada pantalla se carga bajo demanda: el paquete inicial se queda en el
// armazón y el tablero, que es lo único que hace falta para empezar a trabajar.
const LoginPage = lazy(() => import('@/routes/auth/login'));
const DashboardPage = lazy(() => import('@/routes/dashboard/dashboard-page'));
const CustomersPage = lazy(() => import('@/routes/customers/customers-page'));
const CustomerDetailPage = lazy(() => import('@/routes/customers/customer-detail-page'));
const TicketsPage = lazy(() => import('@/routes/tickets/tickets-page'));
const TicketDetailPage = lazy(() => import('@/routes/tickets/ticket-detail-page'));
const InboxPage = lazy(() => import('@/routes/inbox/inbox-page'));
const FollowupsPage = lazy(() => import('@/routes/followups/followups-page'));
const WarrantiesPage = lazy(() => import('@/routes/warranties/warranties-page'));
const ProductsPage = lazy(() => import('@/routes/products/products-page'));
const ProductDetailPage = lazy(() => import('@/routes/products/product-detail-page'));
const CampaignsPage = lazy(() => import('@/routes/campaigns/campaigns-page'));
const CampaignDetailPage = lazy(() => import('@/routes/campaigns/campaign-detail-page'));
const ReportsPage = lazy(() => import('@/routes/reports/reports-page'));
const SettingsPage = lazy(() => import('@/routes/settings/settings-page'));
const NotificationsPage = lazy(() => import('@/routes/notifications/notifications-page'));
const NotFoundPage = lazy(() => import('@/routes/not-found'));

function PageFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Spinner label="Cargando la pantalla…" />
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <TooltipProvider delayDuration={300}>
              {/* Enlace para saltar la navegación: obligatorio para quien usa
                  sólo el teclado y no quiere recorrer el menú en cada página. */}
              <a
                href="#contenido"
                className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-tooltip focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:text-primary-fg"
              >
                Saltar al contenido
              </a>

              <RouteErrorBoundary>
                <Suspense fallback={<PageFallback />}>
                  <Routes>
                    <Route path="/login" element={<LoginPage />} />

                    <Route element={<RequireAuth />}>
                      <Route element={<AppShell />}>
                        <Route index element={<DashboardPage />} />

                        <Route path="clientes" element={<CustomersPage />} />
                        <Route path="clientes/:id" element={<CustomerDetailPage />} />

                        <Route path="tickets" element={<TicketsPage />} />
                        <Route path="tickets/:id" element={<TicketDetailPage />} />

                        <Route path="bandeja" element={<InboxPage />} />
                        <Route path="bandeja/:id" element={<InboxPage />} />

                        <Route path="seguimientos" element={<FollowupsPage />} />
                        <Route path="garantias" element={<WarrantiesPage />} />

                        <Route path="productos" element={<ProductsPage />} />
                        <Route path="productos/:id" element={<ProductDetailPage />} />

                        <Route element={<RequireRole roles={ADMIN_ROLES} />}>
                          <Route path="campanias" element={<CampaignsPage />} />
                          <Route path="campanias/:id" element={<CampaignDetailPage />} />
                        </Route>

                        <Route path="reportes" element={<ReportsPage />} />
                        <Route path="notificaciones" element={<NotificationsPage />} />

                        <Route element={<RequireRole roles={ADMIN_ROLES} />}>
                          <Route path="configuracion" element={<SettingsPage />} />
                          <Route path="configuracion/:section" element={<SettingsPage />} />
                        </Route>

                        <Route path="*" element={<NotFoundPage />} />
                      </Route>
                    </Route>
                  </Routes>
                </Suspense>
              </RouteErrorBoundary>

              <Toaster />
            </TooltipProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
